// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions

import {
  recommendationsPanelsLocaleKeys,
  thankYouPageMarketingPanelHeading,
} from '@epicuro-next/constants/features/localization';
import {
  IDynamicBindableValue,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';

import {
  openConfigStripEventHandler,
  recommendationItemClickedEventHandler,
  recommendationItemShownEventHandler,
} from '../../events';
import {
  errorMessage,
  hasError,
  ifIsLoading,
  isLoading,
} from '../../loading-state-wrapper-helpers';
import { conditionalMessage } from '../../misc';
import { getPanelTrackingIdProperty } from '../../panel-tracking-id';
import {
  hasData,
  hasNoItems,
  httpBodyToProductData,
  productData,
} from '../../product-data';
import { getServingConfigProperty } from '../../serving-config';

const panelNameProvider: IDynamicBindableValue = {
  provider: 'id:checkout-root',
  property: 'panelName',
};
const panelStyleProvider: IDynamicBindableValue = {
  provider: 'id:data-container',
  property: 'panelTrackingId',
};

const loadingSkeleton: IDynamicItem = {
  id: 'loading-skeleton',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['h-100'],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  inputs: {
    media: {
      url: 'assets/static-skeleton-screen.png',
    },
    objectFit: 'cover',
  },
};

const header: IDynamicSimpleText = {
  id: 'header',
  contentTypeId: 'dynamic-elements-simple-text',
  inputs: {
    text: thankYouPageMarketingPanelHeading,
  },
  styles: {
    classes: ['marketing-panel__heading'],
  },
};

const mediaBackground: IDynamicItem = {
  id: 'media-background',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['marketing-panel__figure'],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  inputs: {
    media: {
      url: 'https://epicuropumatestimages.epicurotesting.com/images/static/promotions/1b59f5caa20540e6b6796c4ea8387e4b.png?sv=2019-07-07&sr=b&sig=S1CZo2heZlQHjL8xOGDGoRqeFaRLpeP7VMQq7AfNAB8%3D&se=2123-03-27T12%3A18%3A48Z&sp=r&width=1920&mode=max',
    },
    objectFit: 'cover',
  },
};

const itemMediaPreview: IDynamicItem = {
  id: 'item-media-preview',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['marketing-panel__item'],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  inputs: {
    media: {
      provider: 'self',
      property: 'productData.media',
    },
  },
  eventHandlers: {
    onClick: [
      recommendationItemClickedEventHandler(
        panelNameProvider,
        panelStyleProvider,
        { provider: 'self', property: 'productData' },
      ),
      openConfigStripEventHandler('self'),
    ],
    onVisibleOnce: [
      recommendationItemShownEventHandler(
        panelNameProvider,
        panelStyleProvider,
        { provider: 'self', property: 'productData' },
      ),
    ],
  },
};

const itemsArrayRenderer: IDynamicItem = {
  id: 'items-array-renderer',
  contentTypeId: 'dynamic-elements-array-renderer',
  if: [
    'AND',
    [
      [
        'EQ',
        [
          {
            provider: 'parent',
            property: 'isLoading',
          },
          false,
        ],
      ],
      [
        'EQ',
        [
          {
            provider: 'parent',
            property: 'hasError',
          },
          false,
        ],
      ],
    ],
  ],
  data: {
    products: {
      provider: 'parent',
      property: 'productData',
      transform: function (productDataArg: []) {
        return (productDataArg ?? []).slice(0, 5);
      }.toString(),
    },
    loadingStateWrapper: {
      provider: 'parent',
      property: 'loadingStateWrapper',
    },
  },
  styles: {
    classes: ['marketing-panel__items'],
  },
  inputs: {
    propertyToUse: 'productData',
    items$: {
      provider: 'self',
      property: 'products',
    },
    contentType: itemMediaPreview,
  },
};

const isLoadingWrapper: IDynamicItem = {
  id: 'is-loading-wrapper',
  contentTypeId: 'dynamic-collections-renderer',
  if: ifIsLoading(),
  children: [
    {
      contentTypeId: 'dynamic-collections-renderer',
      styles: {
        classes: ['col'],
      },
      children: [loadingSkeleton],
    },
  ],
};

const textMessageClasses = [
  'marketing-panel__items',
  'd-flex',
  'w-100',
  'justify-content-left',
  'text-muted',
];

const noItemsMessage = conditionalMessage(
  'no-items-message',
  'parent',
  'hasNoItems',
  recommendationsPanelsLocaleKeys.noRecommendedItemsReturned,
  {
    classes: textMessageClasses,
  },
);

const errorMessageItem: IDynamicItem = {
  ...errorMessage(),
  styles: {
    classes: textMessageClasses,
  },
};

const marketingPanelWrapper: IDynamicItem = {
  id: 'marketing-panel-wrapper',
  contentTypeId: 'dynamic-collections-renderer',
  styles: {
    classes: [
      'marketing-panel',
      'panel-5-items-with-no-media',
      'h-100',
      'mb-2',
      'd-flex',
    ],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  inputs: {
    items: [
      header,
      mediaBackground,
      itemsArrayRenderer,
      noItemsMessage,
      errorMessageItem,
    ],
  },
};

const isDoneLoadingWrapper: IDynamicItem = {
  contentTypeId: 'dynamic-collections-renderer',
  styles: {
    classes: ['col'],
  },
  children: [marketingPanelWrapper],
};

export const dataContainer: IDynamicItem = {
  id: 'data-container',
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    loadingStateWrapper: {
      provider: 'HttpBodyProvider',
      property: {
        values: [
          panelNameProvider,
          {
            provider: 'GlobalStoreProviderService',
            property: {
              once: true,
              paths: [
                'accounts.activeAccount.domainId',
                'accounts.activeAccount.brandOrg',
                'productBasket.currentPresetValue',
                'basket.tracking-payloads.payloads.purchase-complete',
              ],
            },
          },
        ],
        transform: function ([panelName, data]: [
          string,
          // tslint:disable-next-line:no-any
          [string, string, string, any],
        ]): string {
          const domainId = data[0];
          const brandOrg = data[1];
          const orderType = data[2] === 'now' ? 'reorder' : 'preorder';
          const userEvent = data[3];
          const brandOrgOrDomainId = brandOrg ?? domainId;
          const body = {
            panelType: panelName,
            properties: {
              brandOrg: brandOrgOrDomainId,
              orderType,
              userEvent,
            },
          };

          function createFetchString(bodyArg: object): string {
            const headersJson = JSON.stringify({
              accept: 'application/json, text/plain, */*',
              'accept-language': 'en-US',
              'content-type': 'application/json',
            });
            const bodyJson = JSON.stringify(JSON.stringify(bodyArg));
            const url = '/api/v2/recommendation/suggest';
            const method = 'POST';
            return `fetch("${url}", { "headers": ${headersJson}, "body": ${bodyJson}, "method": "${method}" });`;
          }

          return createFetchString(body);
        }.toString(),
      },
      transform: httpBodyToProductData.toString(),
      wrapWithLoadingState: true,
    },
    isLoading: isLoading(),
    productData: productData(),
    hasError: hasError(),
    hasData: hasData('self'),
    hasNoItems: hasNoItems(),
    ...getServingConfigProperty(),
    ...getPanelTrackingIdProperty({
      page: 'ty',
      location: '',
      type: '4',
      provider: 'gcrv',
      cssVariant: '001',
    }),
  },
  if: [
    'EQ',
    [
      true,
      {
        provider: 'parent',
        property: 'isPayloadReady',
      },
    ],
  ],
  children: [
    {
      id: 'row-wrapper',
      contentTypeId: 'dynamic-collections-renderer',
      styles: {
        classes: ['row', 'mx-2', 'mb-2'],
      },
      children: [isLoadingWrapper, isDoneLoadingWrapper],
    },
  ],
};

export const checkout: IDynamicItem = {
  id: 'checkout-root',
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    panelName: 'ThankYouPage',
    isPayloadReady: {
      provider: 'GlobalStoreProviderService',
      property: {
        paths: 'basket.tracking-payloads.payloads.purchase-complete',
      },
      transform: function (purchaseComplete: unknown): boolean {
        return Boolean(purchaseComplete);
      }.toString(),
    },
  },
  children: [dataContainer],
};
